import { Injectable, computed, signal } from '@angular/core';

/** `unknown` until the first `/auth/me` answers, then `anonymous` or `authenticated`. */
export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

export interface SessionUser {
  readonly id: string;
  readonly displayName: string;
  readonly mustChangePassword: boolean;
}

/**
 * What the client knows about the signed-in company. There is deliberately no id: the API
 * derives the company from the session cookie, and a client that held one could send it.
 */
export interface SessionCompany {
  readonly name: string;
  /** IANA zone every date for this company is shown in. */
  readonly timeZone: string;
}

/** Whose records a person's lists and reports cover. The API still filters every list itself. */
export type DataScope = 'own' | 'reports' | 'department' | 'all';

/** Everything a sign-in or a restore fills the store with. */
export interface SessionSnapshot {
  readonly user: SessionUser;
  readonly company: SessionCompany;
  /** Permission codes held, `Hrm.<Resource>.<Action>`. */
  readonly permissions: readonly string[];
  /**
   * Role display names, for showing in the UI only. Access is decided by permissions, never
   * by role name. Empty until the API returns roles.
   */
  readonly roles: readonly string[];
  /** Feature flags that are on for this company. */
  readonly flags: readonly string[];
  /** Absent until the API returns a scope. */
  readonly dataScope?: DataScope;
}

/**
 * Signal store for the signed-in session. It only holds state; `AuthService` is what talks
 * to the API and fills it. Holds no token: the server keeps those in HttpOnly cookies.
 */
@Injectable({ providedIn: 'root' })
export class SessionStore {
  private readonly state = signal<SessionSnapshot | null>(null);
  private readonly statusState = signal<SessionStatus>('unknown');

  readonly status = this.statusState.asReadonly();
  readonly isAuthenticated = computed(() => this.statusState() === 'authenticated');
  readonly user = computed(() => this.state()?.user ?? null);
  readonly company = computed(() => this.state()?.company ?? null);
  readonly roles = computed(() => this.state()?.roles ?? []);
  readonly permissions = computed<ReadonlySet<string>>(
    () => new Set(this.state()?.permissions ?? []),
  );
  readonly dataScope = computed<DataScope | null>(() => this.state()?.dataScope ?? null);
  readonly flags = computed<ReadonlySet<string>>(() => new Set(this.state()?.flags ?? []));

  set(snapshot: SessionSnapshot): void {
    this.state.set(snapshot);
    this.statusState.set('authenticated');
  }

  /** Signed out, or never signed in. Drops every permission and flag. */
  clear(): void {
    this.state.set(null);
    this.statusState.set('anonymous');
  }
}
